/**
 * CommitToPlanVersionModal — Doc 104 Steps 7→8 bridge.
 *
 * Presents a dialog to commit a PlanningEngineOutput to the Governance Layer:
 *   1. Select a plan cycle (or pre-filled from scenario.planCycleId)
 *   2. Enter a change reason
 *   3. Call commitEngineToPlanVersion → ONE server transaction (migration 764)
 *      creating the plan_version, its plan_records and governance rows — all
 *      or nothing, so there is no partial state to retry.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, GitMerge, AlertCircle, CheckCircle2, X } from 'lucide-react';
import { listPlanCycles } from '../../../lib/planningService';
import {
  commitEngineToPlanVersion,
  type PlanVersionChangeReason,
} from '../../../lib/planningEngineService';
import type { PlanningEngineOutput } from '../../../lib/planningEngineModule.types';
import { isPlanCycleCommitTarget, type PlanCycle } from '../../../types/planning';
import { COMMIT_TO_PLAN_VERSION_LABEL } from '../../../lib/planning/vocabulary';

// Must match the plan_version_change_reason Postgres enum exactly — there is
// no 'engine_commit' value in that enum, so defaulting to it (as this modal
// used to) made every commit whose reason wasn't manually changed fail with
// `invalid input value for enum plan_version_change_reason: "engine_commit"`.
const CHANGE_REASONS: { value: PlanVersionChangeReason; label: string }[] = [
  { value: 'other', label: 'New engine run proposed' },
  { value: 'constraint_shift', label: 'Constraint shift' },
  { value: 'assumption_break', label: 'Assumption break' },
  { value: 'scope_adjustment', label: 'Scope adjustment' },
];

interface Props {
  output: PlanningEngineOutput;
  prefilledCycleId?: string | null;
  onClose: () => void;
}

export default function CommitToPlanVersionModal({ output, prefilledCycleId, onClose }: Props) {
  const navigate = useNavigate();
  const [cycles, setCycles] = useState<PlanCycle[]>([]);
  const [loadingCycles, setLoadingCycles] = useState(true);
  const [selectedCycleId, setSelectedCycleId] = useState<string>(prefilledCycleId ?? '');
  const [changeReason, setChangeReason] = useState<PlanVersionChangeReason>('other');
  const [committing, setCommitting] = useState(false);
  const [commitError, setCommitError] = useState<string | null>(null);
  const [commitSuccess, setCommitSuccess] = useState<{
    versionId: string;
    recordCount: number;
    initiativesCreated: number;
    /** Included priorities with no item in this workspace — recorded, no initiative. */
    skippedCount: number;
  } | null>(null);

  useEffect(() => {
    // All cycles shown for context; closed/superseded ones are not commit targets.
    listPlanCycles().then(({ data }) => {
      setCycles(data);
      if (!selectedCycleId) {
        const firstOpen = data.find((c) => isPlanCycleCommitTarget(c.status));
        if (firstOpen) setSelectedCycleId(firstOpen.id);
      }
      setLoadingCycles(false);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedCycle = cycles.find((c) => c.id === selectedCycleId) ?? null;
  const commitBlocked = selectedCycle != null && !isPlanCycleCommitTarget(selectedCycle.status);

  const handleCommit = useCallback(async () => {
    if (!selectedCycleId || commitBlocked) return;
    setCommitting(true);
    setCommitError(null);

    const result = await commitEngineToPlanVersion({
      planCycleId: selectedCycleId,
      output,
      changeReason,
    });

    setCommitting(false);

    if (result.error) {
      setCommitError(result.error);
      return;
    }

    setCommitSuccess({
      versionId: result.planVersionId!,
      recordCount: result.recordCount,
      initiativesCreated: result.initiativesCreated,
      skippedCount: result.skippedPriorityIds.length,
    });
  }, [selectedCycleId, commitBlocked, output, changeReason]);

  const handleNavigateToCycle = () => {
    // The commit is a governance transition (a new plan version) — land on
    // the tab that shows it, not the plan overview.
    navigate(`/planning/plans/${selectedCycleId}/governance`);
    onClose();
  };

  const exec = output.executiveSummary;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] shadow-2xl w-full max-w-lg">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--rule)] dark:border-[var(--rule)]">
          <div className="flex items-center gap-2">
            <GitMerge className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            <h2 className="text-base font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
              {COMMIT_TO_PLAN_VERSION_LABEL}
            </h2>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)]">
            <X className="h-4 w-4 text-[var(--text-ter)]" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {commitSuccess ? (
            /* Success state */
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 bg-[var(--green-dim)] dark:bg-[var(--green)]/20 border border-[var(--green)] dark:border-[var(--green)]">
                <CheckCircle2 className="h-5 w-5 text-[var(--green)] dark:text-[var(--text-ter)] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-[var(--green)] dark:text-[var(--text-ter)]">
                    Plan version created
                  </p>
                  <p className="text-xs text-[var(--green)] dark:text-[var(--text-ter)] mt-0.5">
                    {commitSuccess.recordCount} plan record{commitSuccess.recordCount !== 1 ? 's' : ''} and{' '}
                    {commitSuccess.initiativesCreated} committed priorit{commitSuccess.initiativesCreated === 1 ? 'y' : 'ies'} saved.
                    Version ID: <code className="font-mono">{commitSuccess.versionId.slice(0, 8)}…</code>
                  </p>
                  {commitSuccess.skippedCount > 0 && (
                    <p className="text-xs text-[var(--text-sec)] mt-1">
                      {commitSuccess.skippedCount} included priorit{commitSuccess.skippedCount === 1 ? 'y has' : 'ies have'} no
                      item in this workspace, so {commitSuccess.skippedCount === 1 ? 'it is' : 'they are'} recorded in the
                      version but not added to the plan&apos;s committed work.
                    </p>
                  )}
                  <p className="text-xs text-[var(--text-sec)] mt-1">
                    Next: review it on Governance and authorize it as the plan of record.
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleNavigateToCycle}
                  className="flex-1 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium"
                >
                  View governance
                </button>
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-pri)] border border-[var(--rule)] dark:border-[var(--rule)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]"
                >
                  Close
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Engine output preview */}
              <div className="flex gap-3 text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)]">
                <span className="px-2 py-1 bg-[var(--hover-tint)] dark:bg-[var(--bg)] rounded">
                  Mode: <strong className="text-[var(--text-sec)] dark:text-[var(--text-pri)]">{output.planningMode}</strong>
                </span>
                <span className="px-2 py-1 bg-[var(--green-dim)] dark:bg-[var(--green)]/20 text-[var(--green)] dark:text-[var(--text-ter)] rounded">
                  {exec.includedCount} included
                </span>
                <span className="px-2 py-1 bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/20 text-[var(--mustard)] dark:text-[var(--mustard)] rounded">
                  {exec.deferredCount} out - capacity
                </span>
                {exec.blockedCount > 0 && (
                  <span className="px-2 py-1 bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20 text-[var(--coral)] dark:text-[var(--coral)] rounded">
                    {exec.blockedCount} blocked
                  </span>
                )}
              </div>

              {/* Plan cycle selector */}
              <div className="space-y-1.5">
                <label className="block text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-pri)]">
                  Target plan cycle
                </label>
                {loadingCycles ? (
                  <div className="flex items-center gap-2 text-sm text-[var(--text-ter)]">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading cycles…
                  </div>
                ) : cycles.length === 0 ? (
                  <p className="text-xs text-[var(--coral)] dark:text-[var(--coral)]">
                    No plan cycles found. Create a plan cycle in Planning first.
                  </p>
                ) : (
                  <select
                    value={selectedCycleId}
                    onChange={(e) => setSelectedCycleId(e.target.value)}
                    className="w-full text-sm border border-[var(--rule)] dark:border-[var(--rule)] px-3 py-2 bg-[var(--bg-raised)] dark:bg-[var(--bg)] text-[var(--text-pri)] dark:text-[var(--text-pri)] focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {cycles.map((c) => (
                      <option key={c.id} value={c.id} disabled={!isPlanCycleCommitTarget(c.status)}>
                        {c.name} — {c.status}
                      </option>
                    ))}
                  </select>
                )}
                {commitBlocked && (
                  <p className="text-xs text-[var(--mustard)] dark:text-[var(--mustard)]">
                    This cycle is {selectedCycle?.status} — it can no longer receive plan versions.
                    Simulation stays open; pick an open cycle (or rebind the scenario) to propose.
                  </p>
                )}
              </div>

              {/* Change reason */}
              <div className="space-y-1.5">
                <label className="block text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-pri)]">
                  Change reason
                </label>
                <select
                  value={changeReason}
                  onChange={(e) => setChangeReason(e.target.value as PlanVersionChangeReason)}
                  className="w-full text-sm border border-[var(--rule)] dark:border-[var(--rule)] px-3 py-2 bg-[var(--bg-raised)] dark:bg-[var(--bg)] text-[var(--text-pri)] dark:text-[var(--text-pri)] focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {CHANGE_REASONS.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>

              {commitError && (
                <div className="flex items-start gap-2 p-3 bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20 border border-[var(--coral)] dark:border-[var(--coral)]">
                  <AlertCircle className="h-4 w-4 text-[var(--coral)] flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-[var(--coral)] dark:text-[var(--coral)]">{commitError}</p>
                </div>
              )}

              <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                This creates an immutable plan version in the selected cycle. The engine output,
                its plan records and the committed priorities are saved together — all or nothing.
              </p>

              {/* Actions */}
              <div className="flex gap-3 pt-1">
                <button
                  onClick={() => void handleCommit()}
                  disabled={committing || !selectedCycleId || cycles.length === 0 || commitBlocked}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white text-sm font-medium"
                >
                  {committing ? (
                    <><Loader2 className="h-4 w-4 animate-spin" />Creating version…</>
                  ) : (
                    <><GitMerge className="h-4 w-4" />{COMMIT_TO_PLAN_VERSION_LABEL}</>
                  )}
                </button>
                <button
                  onClick={onClose}
                  disabled={committing}
                  className="px-4 py-2 text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-pri)] border border-[var(--rule)] dark:border-[var(--rule)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]"
                >
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
